import { AddOn } from 'store/resource/types';
import { BookingAddOnFormResponse, VendorAddOnDto } from './types';

/*
 * What the tenant already ordered, expressed as a menu selection. The amend submits `items` as the
 * complete desired selection, not a delta — helix replaces every `resource_booking_add_ons` row with
 * what it is handed — so a picker that opens empty on a booking that already has an order wipes it on
 * the first submit. The join is on the catalog item uuid lower-cased on both sides: it is stored
 * lower-cased and collates case-insensitively in MySQL, which JS string identity does not.
 */

export interface SeedMatch {
  addOn: AddOn;
  quantity: number;
}

export interface AddOnSeed {
  matches: Array<SeedMatch>;
  /* One response, not one per row: the slice holds a single `form.values` for the chosen party, so
   * the first row carrying answers is the one that can be honoured. */
  formResponse: BookingAddOnFormResponse | null;
}

export const NO_SEED: AddOnSeed = { matches: [], formResponse: null };

/* Pricing is left to the caller, so bands stay known in one place: the shared line builder. */
export const buildAddOnSeed = (
  vendorAddOns: Array<VendorAddOnDto> | undefined | null,
  menu: Array<AddOn> | undefined | null,
): AddOnSeed => {
  if (!vendorAddOns?.length || !menu?.length) {
    return NO_SEED;
  }

  const byItemUuid = new Map<string, AddOn>();

  menu.forEach(addOn => {
    const key = addOn?.id?.toLowerCase();

    /* First wins: a menu that lists one product twice is still one product. */
    if (key && !byItemUuid.has(key)) {
      byItemUuid.set(key, addOn);
    }
  });

  const quantities = new Map<string, number>();
  let formResponse: BookingAddOnFormResponse | null = null;

  vendorAddOns.forEach(row => {
    const key = row?.catalog_item_uuid?.toLowerCase();
    const quantity = Number(row?.quantity);

    /* An ordered row with no menu row is dropped: helix filters the menu to active products, so one
     * withdrawn since the order was placed has nowhere to render and no band to price against. */
    if (!key || !byItemUuid.has(key) || !Number.isFinite(quantity) || quantity <= 0) {
      return;
    }

    /* Two rows for one product is one product ordered twice, not two lines the picker can draw. */
    quantities.set(key, (quantities.get(key) ?? 0) + quantity);

    if (!formResponse && row.form_response && Object.keys(row.form_response.values ?? {}).length > 0) {
      formResponse = row.form_response;
    }
  });

  /* Ordered by the menu, not by the order: the picker draws its sections off the menu. */
  const matches = menu.reduce<Array<SeedMatch>>((acc, addOn) => {
    const quantity = quantities.get(addOn?.id?.toLowerCase() ?? '');

    return quantity ? [...acc, { addOn, quantity }] : acc;
  }, []);

  return { matches, formResponse };
};
